// Label names hold formatted HTML from the label editor (bold, italic,
// underline, sizes and line breaks), or plain text in older labels

// Kept formatting tags; `p`, `strike` and `font` are converted, others are unwrapped
const ALLOWED_TAGS = new Set(['B', 'STRONG', 'I', 'EM', 'U', 'S', 'BR', 'DIV', 'SPAN']);
const BLOCK_TAGS = new Set(['DIV', 'P']);
// Sizes of the editor size command (`<font size="1-7">`), relative to the label font size
const FONT_SIZES = ['0.6em', '0.8em', '1em', '1.15em', '1.4em', '1.8em', '2.4em'];

function parse(html: string) {
  return new DOMParser().parseFromString(`<body>${html}</body>`, 'text/html').body;
}

/**
  * HTML of a label name: names without formatting tags are plain text (older
  * labels, or editor text with entities only), with their line breaks
  */
export function labelNameToHtml(name: string): string {
  if (/<\/?(b|strong|i|em|u|s|strike|br|div|p|span|font)\b/i.test(name)) return name;
  return name.replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/\r?\n/g, '<br>');
}

const parseName = (name: string) => parse(labelNameToHtml(name));

// Copy of `node` with only the allowed tags, and no attributes
function sanitizeNode(node: Node, doc: Document): Node[] {
  if (node.nodeType === Node.TEXT_NODE) return [doc.createTextNode(node.textContent ?? '')];
  if (!(node instanceof Element)) return [];

  const children = () => Array.from(node.childNodes).flatMap((child) => sanitizeNode(child, doc));
  let element: HTMLElement;
  if (node.tagName === 'FONT') {
    element = doc.createElement('span');
    const size = FONT_SIZES[Number(node.getAttribute('size')) - 1];
    if (size) element.style.fontSize = size;
  } else if (node.tagName === 'P') {
    element = doc.createElement('div');
  } else if (node.tagName === 'STRIKE') {
    element = doc.createElement('s');
  } else if (ALLOWED_TAGS.has(node.tagName)) {
    element = doc.createElement(node.tagName.toLowerCase());
    // Sizes already converted (stored names)
    const size = node instanceof HTMLElement ? node.style.fontSize : '';
    if (node.tagName === 'SPAN' && FONT_SIZES.includes(size)) element.style.fontSize = size;
  } else {
    return children();
  }
  element.append(...children());
  return [element];
}

const isBlank = (node: Node): boolean => (
  node.nodeName === 'BR'
  || (node.nodeType === Node.TEXT_NODE && !node.textContent?.trim())
  || (BLOCK_TAGS.has(node.nodeName) && Array.from(node.childNodes).every(isBlank))
);

/** Sanitized HTML of a label name, breaking lines only where the user did */
export function labelHtml(name: string | null | undefined): string {
  if (!name) return '';

  const source = parseName(name);
  const body = parse('');
  body.append(...Array.from(source.childNodes).flatMap((node) => sanitizeNode(node, body.ownerDocument)));
  // No leading/trailing breaks or empty lines
  while (body.firstChild && isBlank(body.firstChild)) body.firstChild.remove();
  while (body.lastChild && isBlank(body.lastChild)) body.lastChild.remove();
  body.querySelectorAll('div').forEach((block) => {
    while (block.lastChild && block.lastChild.nodeName === 'BR' && block.childNodes.length > 1) block.lastChild.remove();
  });
  return body.innerHTML;
}

/** Plain text of a label name, in a single line (lists, tooltips, names) */
export function labelText(name: string | null | undefined): string {
  if (!name) return '';

  const body = parseName(name);
  body.querySelectorAll('br').forEach((br) => br.replaceWith(' '));
  body.querySelectorAll('div, p').forEach((block) => {
    block.prepend(' ');
    block.append(' ');
  });
  return (body.textContent ?? '').replace(/\s+/g, ' ').trim();
}
